import { normalizeObjectEncoding } from '@/utils/text-normalization'
import type { Deliverable, Plan, PlanConfig } from '@/components/Configuracao/plansTabTypes'

export const SUPPORT_PRICING_OPTIONS = [
  {
    id: 'FIXO_2000',
    label: 'Fixo mensal',
    description: 'R$ 2.000 por mês',
    baseValue: 2000,
  },
  {
    id: 'BASE_500_MAIS_1_FATURAMENTO',
    label: 'Base + faturamento',
    description: 'R$ 500 por mês + 1% do faturamento',
    baseValue: 500,
    revenueSharePercent: 1,
  },
] as const

export type SupportPricingMode = (typeof SUPPORT_PRICING_OPTIONS)[number]['id']

export function normalizePlan(plan: Plan): Plan {
  return normalizeObjectEncoding(plan)
}

/** Quantidade de itens de escopo (deliverables ou services incluídos). */
export function countPlanScopeItems(plan: Plan): number {
  return getUnifiedDeliverables(plan).length
}

export function getUnifiedDeliverables(plan: Plan | null): Deliverable[] {
  if (!plan?.config) return []
  const cfg = plan.config as PlanConfig & {
    services?: Array<{
      category?: string
      services?: Array<{ name?: string; quantity?: string; hours?: number; isIncluded?: boolean }>
    }>
  }

  if (cfg.deliverables && cfg.deliverables.length > 0) {
    return cfg.deliverables.map((d) => ({
      name: d.name || '',
      category: d.category || '',
      quantity: String(d.quantity || '1'),
      hours: Number(d.hours || 0),
    }))
  }

  if (cfg.services && Array.isArray(cfg.services)) {
    const list: Deliverable[] = []
    for (const cat of cfg.services) {
      const categoryName = cat.category || ''
      if (cat.services && Array.isArray(cat.services)) {
        for (const s of cat.services) {
          if (s.isIncluded !== false) {
            list.push({
              name: s.name || '',
              category: categoryName,
              quantity: String(s.quantity || '1'),
              hours: Number(s.hours || 5),
            })
          }
        }
      }
    }
    return list
  }

  return []
}

export function calculateTotalHours(deliverables: Deliverable[]): number {
  if (!deliverables?.length) return 0
  return deliverables.reduce((acc, d) => acc + (Number(d.hours) || 0), 0)
}

export function formatBRL(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value)
}

export function resolveSupportPricingMode(config?: PlanConfig): SupportPricingMode {
  const mode = config?.supportPricingMode
  return mode === 'BASE_500_MAIS_1_FATURAMENTO' ? mode : 'FIXO_2000'
}

export function supportPricingValue(config?: PlanConfig): number {
  const mode = resolveSupportPricingMode(config)
  if (mode === 'BASE_500_MAIS_1_FATURAMENTO') {
    return Number(config?.monthlyBaseValue ?? 500)
  }
  return Number(config?.monthlyFixedValue ?? 2000)
}

export function supportPricingLabel(config?: PlanConfig): string {
  const option = SUPPORT_PRICING_OPTIONS.find((item) => item.id === resolveSupportPricingMode(config))
  return option?.description ?? SUPPORT_PRICING_OPTIONS[0].description
}

export function extractApiErrorMessage(err: unknown): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const data = (err as { response?: { data?: { error?: { message?: string }; message?: string } } })
      .response?.data
    if (data?.error?.message) return data.error.message
    if (typeof data?.message === 'string') return data.message
  }
  if (err instanceof Error) return err.message
  return 'Erro desconhecido'
}

export function buildPlanApiPayload(
  plan: Plan,
  deliverables: Deliverable[],
  totalHours: number,
  calculatedBaseValue: number,
) {
  const name = (plan.name || '').trim()
  return {
    name,
    description: plan.description?.trim() || undefined,
    category: plan.category?.trim() || undefined,
    type: plan.type || 'RECORRENTE',
    baseValue: Number.isFinite(calculatedBaseValue) ? calculatedBaseValue : 0,
    config: (() => {
      const { services: _legacyServices, ...restConfig } = (plan.config || {}) as PlanConfig & {
        services?: unknown
      }
      return {
        ...restConfig,
        deliverables,
        totalHours,
        suggestedValue: calculatedBaseValue,
      }
    })(),
  }
}

export function isDesenvolvimentoCategory(category?: string): boolean {
  return (category || '').toLowerCase().trim().includes('desenvolv')
}

/** Categoria Desenvolvimento → projeto fechado (Demandas Específicas). */
export function resolvePlanTypeForCategory(
  category: string | undefined,
  currentType?: Plan['type'],
): Plan['type'] {
  if (isDesenvolvimentoCategory(category)) return 'DEMANDAS_ESPECIFICAS'
  return currentType || 'RECORRENTE'
}

export function emptyPlan(defaultCategory = 'Marketing'): Plan {
  return {
    id: '',
    name: '',
    category: defaultCategory,
    description: '',
    type: 'RECORRENTE',
    baseValue: '2000',
    config: {
      deliverables: [],
      supportPricingMode: 'FIXO_2000',
      monthlyFixedValue: 2000,
      monthlyBaseValue: 500,
      revenueSharePercent: 1,
    },
  }
}

export const PLAN_CATEGORY_COLORS: Record<string, { border: string; text: string; bg: string }> = {
  Implantação: { border: '#34bfc7', text: '#34bfc7', bg: 'rgba(52, 191, 199, 0.08)' },
  Plataforma: { border: '#1abc9c', text: '#1abc9c', bg: 'rgba(26, 188, 156, 0.08)' },
  SUPORTE: { border: '#2ecc71', text: '#2ecc71', bg: 'rgba(46, 204, 113, 0.08)' },
  MARKETING: { border: 'var(--brand-primary)', text: 'var(--brand-primary)', bg: 'rgba(94, 106, 210, 0.08)' },
  ROCKET: { border: '#f1c40f', text: '#f1c40f', bg: 'rgba(241, 196, 15, 0.08)' },
  OUTROS: { border: 'var(--text-muted)', text: 'var(--text-muted)', bg: 'rgba(149, 165, 166, 0.08)' },
}

export function groupPlansByCategory(plans: Plan[]): Record<string, Plan[]> {
  const groups: Record<string, Plan[]> = {
    Implantação: [],
    Plataforma: [],
    SUPORTE: [],
    MARKETING: [],
    ROCKET: [],
    OUTROS: [],
  }

  plans.forEach((p) => {
    const cat = (p.category || '').toLowerCase().trim()
    const name = (p.name || '').toLowerCase().trim()

    if (
      cat.includes('plataforma') ||
      name.includes('plataforma') ||
      name.includes('setup plataforma')
    ) {
      groups['Plataforma'].push(p)
    } else if (
      cat.includes('implant') ||
      cat.includes('setup') ||
      cat.includes('ativacao') ||
      cat.includes('ativação') ||
      cat.includes('desenvolvimento') ||
      cat.includes('layout') ||
      cat.includes('ecommerce') ||
      name.includes('implanta') ||
      name.includes('site') ||
      name.includes('cro')
    ) {
      groups['Implantação'].push(p)
    } else if (
      cat.includes('suport') ||
      cat.includes('help') ||
      cat.includes('atendimento') ||
      cat.includes('sac') ||
      name.includes('suporte')
    ) {
      groups['SUPORTE'].push(p)
    } else if (cat.includes('rocket') || name.includes('rocket')) {
      groups['ROCKET'].push(p)
    } else if (
      cat.includes('market') ||
      cat.includes('mkt') ||
      cat.includes('anuncio') ||
      cat.includes('anúncio') ||
      cat.includes('midia') ||
      cat.includes('mídia') ||
      cat.includes('social') ||
      name.includes('marketing') ||
      name.includes('start') ||
      name.includes('evolve') ||
      name.includes('elite') ||
      name.includes('scala')
    ) {
      groups['MARKETING'].push(p)
    } else {
      groups['OUTROS'].push(p)
    }
  })

  Object.keys(groups).forEach((key) => {
    groups[key].sort((a, b) =>
      (a.name || '').localeCompare(b.name || '', 'pt-BR', { sensitivity: 'base' }),
    )
  })

  return groups
}
